import React, { useCallback, useEffect, useState } from 'react';
import '../super-admin.css';
import { 
  Button, 
  Form, 
  Input, 
  InputNumber,
  Modal, 
  Space, 
  Typography, 
  message,
  Card,
  List,
  Avatar,
  Drawer,
  Row,
  Col,
  Tag,
  Select
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  ReloadOutlined,
  UserOutlined,
  EyeOutlined,
  CreditCardOutlined
} from '@ant-design/icons';
import { API_BASE as apiBase, authHeaders } from '../../config/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

// Function to notify other pages about subscription changes
function notifySubscriptionChange() {
  // Dispatch a custom event that other pages can listen to
  window.dispatchEvent(new CustomEvent('subscriptionUpdated', { 
    detail: { timestamp: Date.now() } 
  }));
  
  // Also use localStorage as a fallback for cross-tab communication
  localStorage.setItem('subscriptionUpdateTrigger', Date.now().toString());
}

// Helper function to format feature display
function formatFeature(feature) {
  if (typeof feature === 'string') {
    return feature;
  }
  if (typeof feature === 'object' && feature !== null) {
    if (feature.name) return feature.name;
    if (feature.title) return feature.title;
    return JSON.stringify(feature);
  }
  return String(feature);
}

const SubscriptionPlans = () => {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [userCounts, setUserCounts] = useState({}); // planId -> count
  const [planUsers, setPlanUsers] = useState({}); // planId -> [users]
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [deleteOption, setDeleteOption] = useState('cancel'); // 'cancel', 'reassign', 'force'
  const [reassignPlanId, setReassignPlanId] = useState(null);

  // Fetch user counts for each plan (active subscriptions only)
  const fetchUserCounts = useCallback(async (planList) => {
    try {
      const counts = {};
      await Promise.all(planList.map(async (plan) => {
        try {
          const res = await fetch(`${apiBase}/user-subscriptions?plan_id=${plan.id}&status=active&limit=1000`, {
            headers: { 'Content-Type': 'application/json', ...authHeaders() }
          });
          const body = await res.json();
          if (res.ok && body?.items) {
            // Get unique user IDs (in case a user has multiple subscriptions)
            const uniqueUserIds = [...new Set(body.items.map(item => item.user_id))];
            counts[plan.id] = uniqueUserIds.length;
          } else {
            counts[plan.id] = 0;
          }
        } catch {
          counts[plan.id] = 0;
        }
      }));
      setUserCounts(counts);
    } catch (err) {
      console.error('Error fetching user counts:', err);
    }
  }, []);

  // Fetch all subscription plans (from subscription_plans)
  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/subscription-plans?only_active=1`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Failed to load plans');
      const items = Array.isArray(body?.items) ? body.items : [];
      // Normalize fields for UI
      const normalized = items.map(p => ({
        id: p.id,
        name: p.plan_name,
        price: p.monthly_price,
        features: p.features
      }));
      setPlans(normalized);
      
      // Fetch user counts for each plan
      await fetchUserCounts(normalized);
    } catch (err) {
      message.error('Error loading subscription plans');
      console.error('Fetch plans error:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchUserCounts]);

  // Fetch users for a specific plan (active subscriptions only)
  const fetchPlanUsers = useCallback(async (planId) => {
    try {
      setLoading(true);
      const res = await fetch(`${apiBase}/user-subscriptions?plan_id=${planId}&status=active&limit=1000`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Failed to load plan users');
      
      const subscriptions = Array.isArray(body?.items) ? body.items : [];
      
      // Get unique user IDs and fetch user details
      const uniqueUserIds = [...new Set(subscriptions.map(item => item.user_id))];
      const users = await Promise.all(uniqueUserIds.map(async (userId) => {
        try {
          const userRes = await fetch(`${apiBase}/admin/users`, {
            headers: { 'Content-Type': 'application/json', ...authHeaders() }
          });
          const userBody = await userRes.json();
          if (userRes.ok && Array.isArray(userBody)) {
            const user = userBody.find(u => u.id === userId);
            if (user) {
              // Find the subscription details for this user and plan
              const subscription = subscriptions.find(s => s.user_id === userId);
              return {
                ...user,
                subscription: subscription || null
              };
            }
          }
          return null;
        } catch {
          return null;
        }
      }));
      
      const validUsers = users.filter(Boolean);
      setPlanUsers(prev => ({ ...prev, [planId]: validUsers }));
      return validUsers;
    } catch (err) {
      message.error('Error loading plan users');
      console.error('Fetch plan users error:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Listen for subscription updates from other pages
  useEffect(() => {
    const handleSubscriptionUpdate = () => {
      // Refresh plans data when subscriptions are updated elsewhere
      fetchPlans();
    };

    const handleStorageChange = (e) => {
      if (e.key === 'subscriptionUpdateTrigger') {
        handleSubscriptionUpdate();
      }
    };

    // Listen for custom events (same tab)
    window.addEventListener('subscriptionUpdated', handleSubscriptionUpdate);
    
    // Listen for localStorage changes (cross-tab)
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('subscriptionUpdated', handleSubscriptionUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [fetchPlans]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (plan) => {
    setEditing(plan);
    form.setFieldsValue({
      name: plan.name,
      price: plan.price,
      stripe_price_id: plan.stripe_price_id,
      features: typeof plan.features === 'object' && plan.features !== null 
        ? JSON.stringify(plan.features, null, 2) 
        : plan.features || ''
    });
    setModalOpen(true);
  };

  const handleDeleteClick = (plan) => {
    setPlanToDelete(plan);
    setDeleteOption('cancel');
    setReassignPlanId(null);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!planToDelete) return;
    
    try {
      setLoading(true);
      
      // Build query params based on delete option
      let queryParams = '';
      if (deleteOption === 'reassign' && reassignPlanId) {
        queryParams = `?reassign_to=${reassignPlanId}`;
      } else if (deleteOption === 'force') {
        queryParams = '?force=true';
      }
      
      const res = await fetch(`${apiBase}/subscription-plans/${planToDelete.id}${queryParams}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
      });
      const body = await res.json();
      
      if (!res.ok) {
        // If there are active subscriptions and no option selected
        if (res.status === 400 && body.active_subscriptions) {
          message.warning(body.message);
          // Keep modal open to let user choose an option
          return;
        }
        throw new Error(body.error || 'Delete failed');
      }
      
      message.success(body.message || 'Subscription plan deleted successfully');
      if (body.reassigned > 0) {
        message.info(`${body.reassigned} subscription(s) reassigned to another plan`);
      }
      if (body.deleted_subscriptions > 0) {
        message.warning(`${body.deleted_subscriptions} subscription(s) were deleted`);
      }
      
      setDeleteModalOpen(false);
      setPlanToDelete(null);
      await fetchPlans();
      // Notify other pages about plan changes
      notifySubscriptionChange();
    } catch (err) {
      message.error(err.message || 'Error deleting subscription plan');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      // Parse features JSON
      let features = null;
      if (values.features && values.features.trim()) {
        try {
          features = JSON.parse(values.features);
        } catch {
          throw new Error('Features must be valid JSON');
        }
      }

      const payload = {
        plan_name: values.name,
        plan_type: 'custom',
        monthly_price: values.price,
        stripe_price_id: values.stripe_price_id,
        features: features
      };

      if (editing) {
        const res = await fetch(`${apiBase}/subscription-plans/${editing.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(payload),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || 'Update failed');
        message.success('Subscription plan updated successfully');
      } else {
        const res = await fetch(`${apiBase}/subscription-plans`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(payload),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || 'Create failed');
        message.success('Subscription plan created successfully');
      }

      await fetchPlans();
      setModalOpen(false);
      form.resetFields();
      // Notify other pages about plan changes
      notifySubscriptionChange();
    } catch (err) {
      if (err?.errorFields) return; // antd form validation error
      message.error(err.message || 'Error saving subscription plan');
    } finally {
      setLoading(false);
    }
  };

  const showPlanUsers = async (plan) => {
    setSelectedPlan(plan);
    await fetchPlanUsers(plan.id);
    setDrawerOpen(true);
  };


  return (
    <div className="sa-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>Subscription Plans</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchPlans} disabled={loading}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add New Plan
          </Button>
        </Space>
      </div>

      {plans.length > 0 ? (
        <Row gutter={[16, 16]}>
          {plans.map(plan => (
            <Col xs={24} sm={12} lg={8} xl={6} key={plan.id}>
              <Card 
                hoverable 
                style={{ borderRadius: 14 }} 
                bodyStyle={{ padding: 20 }}
              >
                {/* Header with plan name and price */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937' }}>{plan.name}</div>
                    <Text type="secondary" style={{ fontSize: 24, fontWeight: 700, color: '#059669' }}>
                      ${plan.price || 0}
                    </Text>
                  </div>
                  <div style={{ 
                    width: 44, 
                    height: 44, 
                    borderRadius: 10, 
                    background: '#05966922', 
                    color: '#059669', 
                    display: 'grid', 
                    placeItems: 'center', 
                    fontSize: 20 
                  }}>
                    <CreditCardOutlined />
                  </div>
                </div>

                {/* Stripe Price ID */}
                {plan.stripe_price_id && (
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Stripe Price ID:</Text>
                    <div style={{ 
                      fontSize: '11px', 
                      fontFamily: 'monospace', 
                      color: '#6b7280', 
                      background: '#f3f4f6', 
                      padding: '4px 8px', 
                      borderRadius: 4,
                      wordBreak: 'break-all'
                    }}>
                      {plan.stripe_price_id}
                    </div>
                  </div>
                )}

                {/* Description */}
                {plan.description && (
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.4 }}>
                      {plan.description}
                    </Text>
                  </div>
                )}

                {/* Active Users Statistic */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 12, 
                  padding: '12px 16px', 
                  background: '#f8fafc', 
                  borderRadius: 8, 
                  marginBottom: 16 
                }}>
                  <div style={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: 6, 
                    background: '#1890ff22', 
                    color: '#1890ff', 
                    display: 'grid', 
                    placeItems: 'center', 
                    fontSize: 16 
                  }}>
                    <UserOutlined />
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Active Users</Text>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#1890ff' }}>
                      {userCounts[plan.id] || 0}
                    </div>
                  </div>
                </div>

                {/* Features */}
                {Array.isArray(plan.features) && plan.features.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ fontSize: 13, color: '#374151' }}>Features:</Text>
                    <div style={{ marginTop: 8 }}>
                      {plan.features.slice(0, 3).map((feature, index) => (
                        <div key={index} style={{ 
                          fontSize: '12px', 
                          color: '#6b7280', 
                          marginBottom: 4,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6
                        }}>
                          <div style={{ 
                            width: 4, 
                            height: 4, 
                            borderRadius: '50%', 
                            background: '#059669' 
                          }} />
                          {formatFeature(feature)}
                        </div>
                      ))}
                      {plan.features.length > 3 && (
                        <Text type="secondary" style={{ fontSize: '11px', fontStyle: 'italic' }}>
                          +{plan.features.length - 3} more features
                        </Text>
                      )}
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button 
                    size="small" 
                    icon={<EyeOutlined />} 
                    onClick={() => showPlanUsers(plan)}
                    disabled={!userCounts[plan.id]}
                    style={{ flex: 1, minWidth: 'auto' }}
                  >
                    Users
                  </Button>
                  <Button 
                    size="small" 
                    icon={<EditOutlined />} 
                    onClick={() => openEdit(plan)}
                    type="primary"
                    ghost
                  >
                    Edit
                  </Button>
                  <Button 
                    size="small" 
                    icon={<DeleteOutlined />} 
                    danger
                    onClick={() => handleDeleteClick(plan)}
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Text type="secondary">No subscription plans found. Create your first plan to get started.</Text>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        title={editing ? 'Edit Subscription Plan' : 'Create New Subscription Plan'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        okText={editing ? 'Update' : 'Create'}
        confirmLoading={loading}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item 
            name="name" 
            label="Plan Name" 
            rules={[{ required: true, message: 'Please enter a plan name' }]}
          >
            <Input placeholder="e.g., Basic Plan, Premium Plan" />
          </Form.Item>
          
          <Form.Item 
            name="price" 
            label="Price (USD)"
            rules={[{ type: 'number', min: 0, message: 'Price must be a positive number' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="0.00"
              min={0}
              step={0.01}
              precision={2}
              addonBefore="$"
            />
          </Form.Item>
          
          <Form.Item 
            name="stripe_price_id" 
            label="Stripe Price ID"
            tooltip="Enter the Stripe price ID for this subscription plan"
          >
            <Input
              placeholder="price_xxxxxxxxxxxxxx"
            />
          </Form.Item>
          
          <Form.Item 
            name="features" 
            label="Features (JSON)"
            tooltip="Enter features as JSON object or array. Example: {&quot;feature1&quot;: true, &quot;feature2&quot;: &quot;unlimited&quot;}"
          >
            <TextArea
              rows={6}
              placeholder='{"analytics": true, "users": "unlimited", "support": "24/7"}'
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Users Drawer */}
      <Drawer
        title={
          <div>
            <Text strong>{selectedPlan?.name} - Users</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '14px' }}>
              {userCounts[selectedPlan?.id] || 0} active users
            </Text>
          </div>
        }
        placement="right"
        width={500}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        {selectedPlan && (
          <List
            dataSource={planUsers[selectedPlan.id] || []}
            loading={loading}
            renderItem={(user) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} />}
                  title={user.name || 'Unknown User'}
                  description={
                    <div>
                      <div>{user.email}</div>
                      <div style={{ marginTop: 4 }}>
                        <Tag color="blue">{user.role || 'User'}</Tag>
                        {user.subscription && (
                          <div style={{ marginTop: 4, fontSize: '12px', color: '#666' }}>
                            <div>
                              Start: {user.subscription.start_date ? 
                                new Date(user.subscription.start_date).toLocaleDateString() : 'N/A'}
                            </div>
                            <div>
                              End: {user.subscription.end_date ? 
                                new Date(user.subscription.end_date).toLocaleDateString() : 'Active'}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
            locale={{ emptyText: 'No users found for this plan' }}
          />
        )}
      </Drawer>

      {/* Delete Confirmation Modal */}
      <Modal
        title={
          <Space>
            <DeleteOutlined style={{ color: '#ff4d4f' }} />
            <span>Delete Subscription Plan</span>
          </Space>
        }
        open={deleteModalOpen}
        onOk={handleDelete}
        onCancel={() => {
          setDeleteModalOpen(false);
          setPlanToDelete(null);
        }}
        okText="Delete"
        okButtonProps={{ 
          danger: true,
          disabled: deleteOption === 'cancel' || (deleteOption === 'reassign' && !reassignPlanId),
          loading: loading
        }}
        width={600}
      >
        {planToDelete && (
          <div>
            <Text strong style={{ fontSize: 16 }}>
              Are you sure you want to delete "{planToDelete.plan_name}"?
            </Text>
            
            {userCounts[planToDelete.id] > 0 && (
              <div style={{ marginTop: 16, padding: 16, background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8 }}>
                <Text type="warning" strong>
                  ⚠️ Warning: This plan has {userCounts[planToDelete.id]} active subscription(s)
                </Text>
                <div style={{ marginTop: 12 }}>
                  <Text>Please choose how to handle existing subscriptions:</Text>
                  
                  <div style={{ marginTop: 12 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Card 
                        size="small"
                        style={{ 
                          cursor: 'pointer',
                          border: deleteOption === 'reassign' ? '2px solid #1890ff' : '1px solid #d9d9d9'
                        }}
                        onClick={() => setDeleteOption('reassign')}
                      >
                        <Space>
                          <input 
                            type="radio" 
                            checked={deleteOption === 'reassign'} 
                            onChange={() => setDeleteOption('reassign')}
                          />
                          <div>
                            <Text strong>Reassign to another plan (Recommended)</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Move all users to a different subscription plan
                            </Text>
                          </div>
                        </Space>
                        {deleteOption === 'reassign' && (
                          <div style={{ marginTop: 12 }}>
                            <Select
                              placeholder="Select plan to reassign users to"
                              style={{ width: '100%' }}
                              value={reassignPlanId}
                              onChange={setReassignPlanId}
                              options={plans
                                .filter(p => p.id !== planToDelete.id)
                                .map(p => ({
                                  label: `${p.plan_name} - $${p.monthly_price || 0}/mo`,
                                  value: p.id
                                }))}
                            />
                          </div>
                        )}
                      </Card>
                      
                      <Card 
                        size="small"
                        style={{ 
                          cursor: 'pointer',
                          border: deleteOption === 'force' ? '2px solid #ff4d4f' : '1px solid #d9d9d9'
                        }}
                        onClick={() => setDeleteOption('force')}
                      >
                        <Space>
                          <input 
                            type="radio" 
                            checked={deleteOption === 'force'} 
                            onChange={() => setDeleteOption('force')}
                          />
                          <div>
                            <Text strong style={{ color: '#ff4d4f' }}>Force delete (Not recommended)</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Delete plan and all user subscriptions permanently
                            </Text>
                          </div>
                        </Space>
                      </Card>
                    </Space>
                  </div>
                </div>
              </div>
            )}
            
            {(!userCounts[planToDelete.id] || userCounts[planToDelete.id] === 0) && (
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">
                  This plan has no active subscriptions and can be safely deleted.
                </Text>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SubscriptionPlans;